import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

import Error from '@mui/icons-material/Error';
import Info from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';

import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { accountsSelectors } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { tracker } from 'app-components/utils/core';

import ConnectFI from 'assets/images/addFI/ConnectFI.png';
import MintAccountRowLink from 'components/Dialogs/AccountDiscovery/InstitutionAccountsUpdater/NewAccountRow/MintAccountLinking';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogForm from 'components/Dialogs/StdDialogForm';
import QIllustration from 'components/QIllustration';
import QButton from 'components/QButton';
import QTip from 'components/QuickenControls/QTip';
import { pfmImportMultiAccountImport } from 'data/import/importActions';
import type { MultiAccountPayloadType, SuccessResponseType } from 'data/import/importTypes';

import type Screens from './screens';

const useStyles = makeStyles()((theme) => ({
  statusContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  boldText: {
    fontWeight: 500,
  },
  successRoot: {
    height: 440,
    paddingRight: 42,
    paddingLeft: 42,
  },
  linkBox: {
    margin: '24px 132px 0px 132px',
    display: 'flex',
    backgroundColor: theme.palette.info.light,
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  connectLinkBox: {
    display: 'flex',
    backgroundColor: theme.palette.info.light,
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  connectFinishLater: {
    textAlign: 'center',
    margin: '0px 132px 24px 132px',
    display: 'flex',
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  finishLater: {
    textAlign: 'center',
    margin: '34px 132px 24px 132px',
    display: 'flex',
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  marginRightAuto: {
    marginRight: 'auto',
  },
  matchHeader: {
    marginBottom: 8,
    paddingBottom: 24,
  },
  matchHeaderText: {
    width: 820,
  },
  successHeight: {
    height: 210,
    paddingTop: 150,
  },
  accountRowDiv: {
    height: 445,
    overflow: 'auto',
  },
  infoIcon: {
    marginLeft: 2,
    marginRight: 2,
    height: 18,
    color: theme.palette.info.main,
  },
  infoBox: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: 8,
    marginTop: 8,
    borderRadius: 8,
    width: '100%',
    backgroundColor: theme.palette.info.light,
  },
  errorBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
    marginTop: 8,
    borderRadius: 8,
    width: '100%',
    backgroundColor: theme.palette.error.light,
  },
  errorIcon: {
    marginLeft: 2,
    marginRight: 2,
    height: 18,
    color: theme.palette.error.main,
  },
  tenThousandTxns: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  imageWidth: {
    width: 302,
    marginRight: 24,
  },
}));

const MintMatchDialog = (props: {
  dispatchViewStateAction: React.Dispatch<Screens>;
  successPage?: boolean;
  onFinishLater: (() => void) | undefined;
  onHandleCloseOverride: () => void;
  accounts?: null | { accountName: string; enabled: boolean; id: string; txnCount: number }[];
  setMintSuccessData?: React.Dispatch<React.SetStateAction<SuccessResponseType | null>>;
  mintSuccessData?: SuccessResponseType | null;
  isGSFImport?: boolean;
  setIsLinking?: React.Dispatch<React.SetStateAction<boolean>>;
  extensionSuccessPage?: boolean;
  handleMintImportMore: () => void;
}) => {
  const {
    dispatchViewStateAction,
    successPage,
    onFinishLater,
    onHandleCloseOverride,
    accounts,
    setMintSuccessData,
    mintSuccessData,
    isGSFImport,
    setIsLinking,
    extensionSuccessPage,
    handleMintImportMore,
  } = props;
  const { classes } = useStyles();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [_gsfScreen, updateGsfScreen] = usePreferenceV2('gsfScreen', 'completed_gsf');
  const dispatch = useDispatch();
  const accountsById = useSelector(accountsSelectors.getAccountsById);
  // @ts-expect-error FIXME TS2345
  const mintImportAsync = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'mintImportAsync'));

  const handleContinueToLink = () => {
    onHandleCloseOverride();
    if (setIsLinking) {
      setIsLinking(true);
    } else {
      dispatch(
        accountDiscoveryActions.doAccountDiscovery({
          initiator: 'getting started',
          mode: 'GSF',
        }),
      );
    }
  };

  const handleDone = () => {
    onHandleCloseOverride();
    if (isGSFImport && !setIsLinking) {
      navigate('/');
      updateGsfScreen('completed_gsf');
    }
  };

  const handleFinishLater = () => {
    onHandleCloseOverride();
    if (onFinishLater) {
      onFinishLater();
    }
  };

  if (successPage) {
    return (
      <>
        <div className={classes.successRoot}>
          <div
            className={classNames(
              classes.statusContainer,
              mintSuccessData?.unlinkedAccountsCount === 0 ? classes.successHeight : '',
            )}
          >
            <QIllustration name='thumbsUp' size='lg' />
            <div>
              <Typography variant='titleMedium'>
                {mintSuccessData?.totalTxnCount || 0} Transactions imported from Mint!
              </Typography>
              {mintSuccessData && (
                <Typography variant='paragraphRegular'>
                  Your imported history starts from {DateTime.fromISO(mintSuccessData?.oldestTxnDate).toLocaleString()}.
                </Typography>
              )}
            </div>
          </div>
          {mintSuccessData && mintSuccessData.unlinkedAccountsCount > 0 ? (
            <>
              <div className={classes.linkBox}>
                <Typography variant='titleMedium'>
                  <span className={classes.boldText}>Next, link your Financial Institutions</span> so Simplifi stays
                  up-to-date with <span className={classes.boldText}>your latest transactions.</span>
                </Typography>
              </div>
              <div className={classes.finishLater}>
                <Typography variant='titleMedium'>
                  If you choose to finish this later, you can link your Financial Institutions under Settings {'>'}{' '}
                  Accounts.
                </Typography>
              </div>
            </>
          ) : (
            <div className={classes.finishLater}>
              <Typography variant='paragraphRegular'>
                Choose Link Institutions to keep Simplifi up-to-date with your latest transactions. Otherwise, choose
                Done to start checking out your finances.
              </Typography>
            </div>
          )}
        </div>
        <DialogActions>
          {mintSuccessData && mintSuccessData.unlinkedAccountsCount > 0 && isGSFImport ? (
            <>
              <QButton
                variant='default'
                onClick={handleMintImportMore}
                className={classes.marginRightAuto}
                disableOutline
              >
                Import More Data
              </QButton>
              <QButton onClick={handleFinishLater} variant='default' disableOutline>
                Finish Later
              </QButton>
              <QButton variant='contained' onClick={handleContinueToLink} startIcon={<LinkIcon />}>
                Link Financial Institutions
              </QButton>
            </>
          ) : (
            <>
              <QButton
                variant='default'
                onClick={handleMintImportMore}
                disableOutline
                className={classes.marginRightAuto}
              >
                Import More Data
              </QButton>
              <QButton variant='default' onClick={handleContinueToLink} startIcon={<LinkIcon />}>
                Link Financial Institutions
              </QButton>
              <QButton variant='contained' onClick={handleDone}>
                Done
              </QButton>
            </>
          )}
        </DialogActions>
      </>
    );
  }

  if (extensionSuccessPage) {
    return (
      <>
        <div className={classes.successRoot}>
          <div
            className={classNames(
              classes.statusContainer,
              mintSuccessData?.unlinkedAccountsCount === 0 ? classes.successHeight : '',
            )}
          >
            <QIllustration name='thumbsUp' size='lg' />
            <div>
              <Typography variant='titleMedium'>
                {mintSuccessData?.totalTxnCount || 0} Transactions imported from Mint!
              </Typography>
              {mintSuccessData && (
                <Typography variant='paragraphRegular'>
                  Your imported history starts from {DateTime.fromISO(mintSuccessData?.oldestTxnDate).toLocaleString()}.
                </Typography>
              )}
            </div>
          </div>
          <div className={classes.connectLinkBox}>
            <img alt='connect-fi' src={ConnectFI} className={classes.imageWidth} />
            <Typography variant='titleMedium'>
              <span className={classes.boldText}>
                Next, you will go to Settings to link your Financial Institutions by selecting Connect
              </span>{' '}
              so Simplifi stays up-to-date with <span className={classes.boldText}>your latest transactions.</span>
            </Typography>
          </div>
          <div className={classes.connectFinishLater}>
            <Typography variant='titleMedium'>
              If you choose to finish this later, you can link your Financial Institutions under Settings {'>'}{' '}
              Accounts.
            </Typography>
          </div>
        </div>
        <DialogActions>
          <>
            <QButton
              variant='default'
              onClick={handleMintImportMore}
              className={classes.marginRightAuto}
              disableOutline
            >
              Import More Data
            </QButton>
            <QButton onClick={handleFinishLater} variant='default' disableOutline>
              Finish Later
            </QButton>
            <QButton
              variant='contained'
              onClick={() => {
                if (!window.location.href.includes('/get-started')) {
                  navigate('/settings/accounts');
                  handleFinishLater();
                } else {
                  handleContinueToLink();
                }
              }}
              startIcon={<LinkIcon />}
            >
              Link Financial Institutions
            </QButton>
          </>
        </DialogActions>
      </>
    );
  }

  // @ts-expect-error FIXME TS7006
  const handleSubmit = (values) => {
    dispatchViewStateAction('mintLoading');
    const accountParams: MultiAccountPayloadType = {
      accounts: Object.values(values)
        .filter((account: any) => account.enabled)
        .map((accountItem: any) => ({
          id: accountItem.id,
          accountName: `${accountItem.accountName}(Imported)`,
          enabled: accountItem.enabled,
          subType: accountItem.type.qcsSubType,
          accountType: accountItem.type.qcsType,
          txnCount: accountItem.txnCount,
          accountId: accountItem.accountId === '0' ? null : accountItem.accountId,
        })),
    };
    if (accountParams) {
      tracker.track(tracker.events.csvImport, { status: 'multi-account-creation', type: 'Mint' });
      actionHelpers
        .bindPromiseAction(
          dispatch,
          pfmImportMultiAccountImport,
        )(accountParams)
        .then((response: any) => {
          if (!mintImportAsync && setMintSuccessData) {
            const responseData = {
              oldestTxnDate: response.data.oldestTxnDate,
              totalTxnCount: response.data.totalTxnCount,
              unlinkedAccountsCount: response.data.unlinkedAccountsCount,
            };
            setMintSuccessData(responseData);
            dispatchViewStateAction('mintSuccess');
            tracker.track(tracker.events.csvImport, { status: 'success', type: 'Mint' });
          }
        })
        .catch((_response) => {
          handleMintImportMore();
        });
    }
  };

  const initialValues = accounts?.reduce(
    (
      accum: Record<
        string,
        { accountName: string; enabled: boolean; id: string; txnCount: number; type: undefined; accountId: string }
      >,
      account,
    ) => {
      accum[account.id] = {
        enabled: true,
        accountName: account.accountName,
        type: undefined,
        accountId: '0', // default so the add as new option shows
        id: account.id,
        txnCount: account.txnCount,
      };
      return accum;
    },
    {},
  );

  // @ts-expect-error FIXME TS7006
  const renderForm = (formikBag) => {
    const { handleSubmit: formikHandleSubmit, values, isValid, submitCount } = formikBag;
    const stagedNames: string[] = [];
    const stagedLinkToIds: string[] = [];
    Object.values(values).forEach((value: any) => {
      if (value.enabled) {
        if (value.accountName) {
          stagedNames.push(value.accountName.toLowerCase());
        }
        if (value.accountId !== '0') {
          stagedLinkToIds.push(value.accountId);
        }
      }
    });
    const importTxnCount: number = Object.values(accounts || {}).reduce((a, b) => a + b.txnCount, 0) || 0;
    return (
      <StdDialogForm component={Form} title='Import from Mint' onSubmit={formikHandleSubmit}>
        <div className={classes.matchHeader}>
          <Typography variant='bodyMedium'>
            We found {accounts?.length} account{accounts?.length === 1 ? '' : 's'}
          </Typography>
          {accountsById.size !== 0 ? (
            <Typography variant='paragraphRegular' className={classes.matchHeaderText}>
              Review the account{accounts?.length === 1 ? '' : 's'} we found for you. You can link existing Simplifi
              accounts to tell us where imported transactions should be placed. Otherwise, choose “Add as New” to finish
              link setup later.
            </Typography>
          ) : (
            <Typography variant='paragraphRegular'>Select the correct type for each account.</Typography>
          )}
          {importTxnCount === 10000 && (
            <QTip
              solidTheme
              arrow
              placement='top'
              title='To manage more than 10,000 transactions in Mint, we suggest exporting in smaller batches defined by date range. Alternatively, you can import your accounts one-by-one.'
            >
              <div className={classes.infoBox}>
                <div className={classes.tenThousandTxns}>
                  <Typography variant='bodyRegular'>Heads up, your CSV has exactly 10,000 transactions.</Typography>
                  <Info className={classes.infoIcon} />
                </div>
                <Typography variant='paragraphRegular'>
                  Mint limits CSV exports to 10,000 transactions automatically, and it looks like your file hits that
                  limit.
                </Typography>
              </div>
            </QTip>
          )}
          {!isValid && submitCount > 0 && (
            <div className={classes.errorBox}>
              <Error className={classes.errorIcon} />
              <Typography variant='bodyRegular'>Check your accounts and fix all errors before continuing.</Typography>
            </div>
          )}
        </div>
        <div className={classes.accountRowDiv}>
          {accounts?.map((account, index) => (
            <MintAccountRowLink
              key={account.id}
              accountsById={accountsById}
              stagedNames={stagedNames}
              index={index}
              account={account}
              stagedLinkToIds={stagedLinkToIds}
              enableLinkTo
              formik={formikBag}
            />
          ))}
        </div>
        <StdDialogActions
          primary={{
            children: 'Continue',
            id: 'add-imported-accounts',
            disabled: !Object.values(values).some((account: any) => account.enabled),
          }}
        />
      </StdDialogForm>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnSubmit
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
    >
      {renderForm}
    </Formik>
  );
};

export default MintMatchDialog;
